import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addMonths, format, parseISO } from "date-fns";
import { ArrowRight, Calendar, CheckCircle2, ClipboardCheck, FileText, Loader2, Plus, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  type ComplianceFramework,
  type ComplianceProgram,
  formatFramework,
  useCompliancePrograms,
  useProgramDetails,
} from "@/hooks/use-compliance-programs";

const frameworkOptions: Array<{
  value: ComplianceFramework;
  label: string;
  description: string;
}> = [
  { value: "SOC2", label: "SOC 2 Type II", description: "Security controls for SaaS vendor reviews" },
  { value: "ISO-27001", label: "ISO 27001", description: "Information security management system" },
  { value: "HIPAA", label: "HIPAA", description: "Administrative and technical safeguards for PHI" },
];

function ProgramCard({ program }: { program: ComplianceProgram }) {
  const navigate = useNavigate();
  const { health, loading } = useProgramDetails(program.id);
  const target = program.target_audit_date ? format(parseISO(program.target_audit_date), "MMM yyyy") : "No date";

  return (
    <Card className="overflow-hidden border-border/80 transition-colors hover:border-primary/40">
      <CardHeader className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{formatFramework(program.framework)}</Badge>
              <Badge variant={program.status === "active" ? "default" : "outline"}>
                {program.status.replace("_", " ")}
              </Badge>
            </div>
            <CardTitle className="text-xl font-semibold tracking-tight">{program.name}</CardTitle>
          </div>
          <Button size="icon" variant="ghost" onClick={() => navigate(`/compliance/programs/${program.id}`)}>
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Readiness</span>
            <span className="font-medium">{loading ? "..." : `${health?.readinessPercent ?? 0}%`}</span>
          </div>
          <Progress value={health?.readinessPercent ?? 0} />
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="rounded-md bg-muted/50 p-3">
            <p className="text-muted-foreground">Controls</p>
            <p className="mt-1 font-semibold">{loading ? "-" : `${health?.metControls ?? 0}/${health?.totalControls ?? 0}`}</p>
          </div>
          <div className="rounded-md bg-muted/50 p-3">
            <p className="text-muted-foreground">Open tasks</p>
            <p className="mt-1 font-semibold">{loading ? "-" : health?.openTasks ?? 0}</p>
          </div>
          <div className="rounded-md bg-muted/50 p-3">
            <p className="text-muted-foreground">Target</p>
            <p className="mt-1 font-semibold">{target}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StartProgramDialog() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { startProgram } = useCompliancePrograms();
  const [open, setOpen] = useState(false);
  const [framework, setFramework] = useState<ComplianceFramework>("SOC2");
  const [targetAuditDate, setTargetAuditDate] = useState(format(addMonths(new Date(), 6), "yyyy-MM-dd"));
  const [scopeNotes, setScopeNotes] = useState("Core SaaS application, production infrastructure, employee access, customer data handling.");
  const [submitting, setSubmitting] = useState(false);

  const selectedFramework = frameworkOptions.find((option) => option.value === framework);

  const handleStart = async () => {
    setSubmitting(true);
    try {
      const programId = await startProgram({
        framework,
        targetAuditDate: targetAuditDate || null,
        scope: {
          notes: scopeNotes,
          framework_label: selectedFramework?.label,
          generated_from: "program_scoping_wizard",
        },
      });
      toast({ title: "Program started", description: "Policies, controls, evidence buckets, and phase tasks were created." });
      setOpen(false);
      navigate(`/compliance/programs/${programId}`);
    } catch (error) {
      console.error("Error starting compliance program:", error);
      toast({
        title: "Could not start program",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <Plus className="mr-2 h-4 w-4" />
          Start new program
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>Start a compliance program</DialogTitle>
          <DialogDescription>
            Choose a framework, confirm scope, and Manageaze will create the initial program workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="space-y-2">
            <Label>Framework</Label>
            <Select value={framework} onValueChange={(value) => setFramework(value as ComplianceFramework)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {frameworkOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">{selectedFramework?.description}</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="target-date">Target audit date</Label>
            <Input id="target-date" type="date" value={targetAuditDate} onChange={(event) => setTargetAuditDate(event.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="scope">Scope confirmation</Label>
            <Textarea
              id="scope"
              value={scopeNotes}
              onChange={(event) => setScopeNotes(event.target.value)}
              rows={4}
            />
          </div>
          <Button className="w-full" onClick={handleStart} disabled={submitting}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            Generate program
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Programs() {
  const { programs, loading, error } = useCompliancePrograms();
  const activePrograms = useMemo(() => programs.filter((program) => program.status !== "archived"), [programs]);

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <div className="grid gap-4 lg:grid-cols-2">
          {[0, 1].map((item) => (
            <Skeleton key={item} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-medium tracking-tight">Compliance Programs</h1>
          <p className="text-muted-foreground">Run framework-specific journeys across policies, controls, evidence, team readiness, and audit prep.</p>
        </div>
        <StartProgramDialog />
      </div>

      {error && <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">{error}</div>}

      {activePrograms.length === 0 ? (
        <Card>
          <CardContent className="grid gap-8 p-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div className="space-y-4">
              <Badge variant="secondary" className="w-fit">No active program</Badge>
              <div className="space-y-2">
                <h2 className="text-2xl font-semibold tracking-tight">Start with a guided framework journey.</h2>
                <p className="max-w-2xl text-muted-foreground">
                  A program creates the control registry, evidence buckets, policy drafts, and phase tasks needed to move toward certification.
                </p>
              </div>
              <StartProgramDialog />
            </div>
            <div className="grid gap-3">
              {[
                { icon: FileText, label: "Policies", text: "Template-derived drafts linked to controls" },
                { icon: ClipboardCheck, label: "Controls", text: "Framework registry scoped to the program" },
                { icon: Calendar, label: "Evidence", text: "Artifacts, owners, due dates, and reminders" },
                { icon: CheckCircle2, label: "Tasks", text: "Phase plan generated into Compliance Tasks" },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3 rounded-md border bg-background p-4">
                  <item.icon className="mt-0.5 h-5 w-5 text-primary" />
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-sm text-muted-foreground">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {activePrograms.map((program) => (
            <ProgramCard key={program.id} program={program} />
          ))}
        </div>
      )}
    </div>
  );
}
